import React, { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faArrowLeft,
  faTimes,
  faPlus,
  faTrash,
  faUpload,
  faImages,
  faLink,
} from "@fortawesome/free-solid-svg-icons";
import { getProjetById, createProjet, updateProjet } from "../../services/api";
import { Projet } from "../../types/types";
import { toast } from "react-toastify";
import AdminLayout from "./AdminLayout";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useLanguage } from "../../context/LanguageContext";
import { getTranslation } from "../../utils/translations";
import { getFileName, resolveMediaUrl } from "../../utils/media";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link", "image"],
    ["clean"],
  ],
};

interface ImageFile extends File {
  preview?: string;
}

const ProjetForm: React.FC = () => {
  const { language } = useLanguage();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // Text states
  const [arabicDesc, setArabicDesc] = useState("");
  const [frenchDesc, setFrenchDesc] = useState("");

  // Images state
  const [imageFiles, setImageFiles] = useState<ImageFile[]>([]);
  const [existingImages, setExistingImages] = useState<{ image: string }[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Projet>();

  useEffect(() => {
    if (isEditMode) {
      const fetchProjet = async () => {
        try {
          setIsLoading(true);
          const response = await getProjetById(parseInt(id!));
          const projet = response.data;

          if (projet) {
            reset({
              titrearabe: projet.titrearabe || "",
              titrefrancais: projet.titrefrancais || "",
              lien: projet.lien || "",
            });

            setArabicDesc(projet.descarabe || "");
            setFrenchDesc(projet.descfrancais || "");

            if (projet.images && projet.images.length > 0) {
              setExistingImages(projet.images);
            }
          }
        } catch (error) {
          console.error("Error fetching project:", error);
          toast.error("حدث خطأ أثناء جلب بيانات المشروع");
        } finally {
          setIsLoading(false);
        }
      };
      fetchProjet();
    }
  }, [id, isEditMode, reset]);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const files = Array.from(e.target.files);
        const validFiles = files.filter((file) =>
          file.type.startsWith("image/"),
        );

        if (validFiles.length !== files.length) {
          toast.error("بعض الملفات ليست صوراً. تم تجاهلها.");
        }

        const newImageFiles = validFiles.map((file) => {
          const imageFile = file as ImageFile;
          imageFile.preview = URL.createObjectURL(file);
          return imageFile;
        });

        setImageFiles((prev) => [...prev, ...newImageFiles]);
      }
    },
    [],
  );

  const handleFileDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const files = event.dataTransfer.files;
      if (files) {
        const fileArray = Array.from(files);
        const validFiles = fileArray.filter((file) =>
          file.type.startsWith("image/"),
        );

        if (validFiles.length !== fileArray.length) {
          toast.error("بعض الملفات ليست صوراً. تم تجاهلها.");
        }

        const newImageFiles = validFiles.map((file) => {
          const imageFile = file as ImageFile;
          imageFile.preview = URL.createObjectURL(file);
          return imageFile;
        });

        setImageFiles((prev) => [...prev, ...newImageFiles]);
      }
      setIsDragOver(false);
    },
    [],
  );

  const removeImage = (index: number) => {
    setImageFiles((prev) => {
      const newFiles = [...prev];
      if (newFiles[index].preview) {
        URL.revokeObjectURL(newFiles[index].preview!);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const removeExistingImage = (index: number) => {
    setExistingImages((prev) => {
      const newImages = [...prev];
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const encodeFileContent = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const encodedContent = reader.result as string;
        resolve(encodedContent);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const onSubmit = async (data: Projet) => {
    setIsLoading(true);
    try {
      // Convert new uploads to encoded payload strings
      const newImages = await Promise.all(
        imageFiles.map(async (file) => ({
          image: await encodeFileContent(file),
        })),
      );

      // Combine existing images with new ones
      const allImages = [
        ...existingImages.map((img) => ({
          image: getFileName(img.image) || img.image,
        })),
        ...newImages,
      ];

      // Prepare payload
      const payload = {
        titrearabe: data.titrearabe,
        titrefrancais: data.titrefrancais,
        descarabe: arabicDesc,
        descfrancais: frenchDesc,
        lien: data.lien,
        images: allImages,
      };

      if (isEditMode) {
        await updateProjet(parseInt(id!), payload);
        toast.success("تم تحديث المشروع بنجاح");
      } else {
        await createProjet(payload);
        toast.success("تمت إضافة المشروع بنجاح");
      }

      navigate("/admin/projets");
    } catch (error: any) {
      console.error("Error saving project:", error);
      const errorMessage =
        error.response?.data?.message ||
        `حدث خطأ أثناء ${getTranslation("save", language)}`;
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Clean up object URLs
  useEffect(() => {
    return () => {
      imageFiles.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [imageFiles]);

  return (
    <AdminLayout>
      <style>
        {`
          .rtl-quill .ql-editor {
            direction: rtl;
            text-align: right;
          }
          .rtl-quill .ql-editor ol, 
          .rtl-quill .ql-editor ul {
            padding-right: 1.5em;
            padding-left: 0;
          }
          .ltr-quill .ql-editor {
            direction: ltr;
            text-align: left;
          }
          .ltr-quill .ql-editor ol, 
          .ltr-quill .ql-editor ul {
            padding-left: 1.5em;
            padding-right: 0;
          }
        `}
      </style>

      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-mauritania-green-dark">
          {isEditMode
            ? "تعديل المشروع"
            : getTranslation("addProject", language)}
        </h1>
        <button
          onClick={() => navigate("/admin/projets")}
          className="bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 transition duration-300"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="ml-2" />
          {getTranslation("back", language)}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Titles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="titrearabe">
                العنوان (عربي) *
              </label>
              <input
                id="titrearabe"
                type="text"
                {...register("titrearabe", {
                  required: "هذا الحقل مطلوب",
                  minLength: {
                    value: 2,
                    message: "يجب أن يكون العنوان على الأقل حرفين",
                  },
                })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green ${
                  errors.titrearabe ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="عنوان المشروع بالعربية"
              />
              {errors.titrearabe && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.titrearabe.message}
                </p>
              )}
            </div>

            <div>
              <label
                className="block text-gray-700 mb-2"
                htmlFor="titrefrancais"
              >
                العنوان (فرنسي) *
              </label>
              <input
                id="titrefrancais"
                type="text"
                {...register("titrefrancais", {
                  required: "هذا الحقل مطلوب",
                  minLength: {
                    value: 2,
                    message: "يجب أن يكون العنوان على الأقل حرفين",
                  },
                })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green ${
                  errors.titrefrancais ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Titre du projet"
              />
              {errors.titrefrancais && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.titrefrancais.message}
                </p>
              )}
            </div>
          </div>

          {/* Link */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2" htmlFor="lien">
              رابط المشروع (اختياري)
            </label>
            <div className="flex items-center">
              <FontAwesomeIcon icon={faLink} className="text-gray-400 ml-2" />
              <input
                id="lien"
                type="url"
                {...register("lien", {
                  pattern: {
                    value:
                      /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
                    message: "يرجى إدخال رابط صحيح",
                  },
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green"
                placeholder="https://www.example.com/projet"
              />
            </div>
            {errors.lien && (
              <p className="text-red-500 text-sm mt-1">{errors.lien.message}</p>
            )}
          </div>

          {/* Descriptions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 mb-2">الوصف (عربي) *</label>
              <div className="rtl-quill">
                <ReactQuill
                  theme="snow"
                  value={arabicDesc}
                  onChange={setArabicDesc}
                  modules={quillModules}
                  className="h-64 mb-16"
                  placeholder="أدخل وصف المشروع باللغة العربية..."
                />
              </div>
              {!arabicDesc.trim() && (
                <p className="text-red-500 text-sm mt-1">هذا الحقل مطلوب</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 mb-2">
                الوصف (فرنسي) *
              </label>
              <div className="ltr-quill">
                <ReactQuill
                  theme="snow"
                  value={frenchDesc}
                  onChange={setFrenchDesc}
                  modules={quillModules}
                  className="h-64 mb-16"
                  placeholder="Entrez la description du projet en français..."
                />
              </div>
              {!frenchDesc.trim() && (
                <p className="text-red-500 text-sm mt-1">هذا الحقل مطلوب</p>
              )}
            </div>
          </div>

          {/* Images Upload */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">
              صور المشروع (يمكن رفع أكثر من صورة)
            </label>

            {/* Drag & Drop Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors mb-4 ${
                isDragOver
                  ? "border-mauritania-green bg-mauritania-green-light"
                  : "border-gray-300 hover:border-mauritania-green"
              }`}
              onDrop={handleFileDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() =>
                document.getElementById("image-files-input")?.click()
              }
            >
              <input
                id="image-files-input"
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />

              <div className="bg-gray-100 p-3 rounded-lg inline-block mb-3">
                <FontAwesomeIcon
                  icon={faUpload}
                  className="text-gray-400 text-2xl"
                />
              </div>
              <p className="text-sm text-gray-600">
                اسحب الصور هنا أو انقر للاختيار
              </p>
              <p className="text-xs text-gray-500 mt-1">
                يمكنك اختيار أكثر من صورة (JPG, PNG, GIF)
              </p>
            </div>

            {/* Selected Images Preview */}
            {(imageFiles.length > 0 || existingImages.length > 0) && (
              <div className="mt-4">
                <h4 className="font-medium text-gray-700 mb-3">
                  الصور المختارة ({imageFiles.length + existingImages.length})
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {/* Existing Images */}
                  {existingImages.map((img, index) => (
                    <div key={`existing-${index}`} className="relative group">
                      <img
                        src={resolveMediaUrl(img.image)}
                        alt={`Existing ${index + 1}`}
                        className="h-32 w-full object-cover rounded-md"
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingImage(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <FontAwesomeIcon icon={faTimes} className="text-xs" />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 text-center">
                        صورة موجودة
                      </div>
                    </div>
                  ))}

                  {/* New Images */}
                  {imageFiles.map((file, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={file.preview}
                        alt={`Preview ${index + 1}`}
                        className="h-32 w-full object-cover rounded-md"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <FontAwesomeIcon icon={faTimes} className="text-xs" />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 text-center truncate">
                        {file.name}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add more images button */}
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() =>
                      document.getElementById("image-files-input")?.click()
                    }
                    className="inline-flex items-center text-mauritania-green hover:text-mauritania-green-dark"
                  >
                    <FontAwesomeIcon icon={faPlus} className="ml-1" />
                    إضافة المزيد من الصور
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading || !arabicDesc.trim() || !frenchDesc.trim()}
              className="bg-mauritania-green text-white py-2 px-6 rounded-md hover:bg-mauritania-green-dark transition duration-300 focus:outline-none focus:ring-2 focus:ring-mauritania-green focus:ring-opacity-50 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faSave} className="ml-2" />
              {isLoading ? getTranslation("saving", language) : "حفظ المشروع"}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default ProjetForm;
